import type {ReactElement} from 'react';
import React, {useRef, useState} from 'react';
import {ResponsivePreviewSwitch} from '@Components/event-wizard/responsive-preview-switch';
import type {PreviewProps} from '@Components/event-wizard/preview/preview.types';
import {PREVIEW_IFRAME_ID} from '@Components/event-wizard/preview/preview.types';
import {
  updatePreviewCountdown,
  updatePreviewCoverPhoto,
  updatePreviewDateTime,
  updatePreviewDescription,
  updatePreviewOrganiserDetails, updatePreviewRegistrationCTAText,
  updatePreviewRegistrationForm,
  updatePreviewTimeZone,
  updatePreviewTitle,
  updatePreviewVenue,
  updatePreviewVenueAddressDetails,
  updatePreviewWatermark,
  updateStyleColor,
  updateStyleFont,
} from '@Components/event-wizard/preview/preview.helper';
import {getGMTLabel} from '@Components/event-wizard/timezone-content/timezone-content.helper';
import {
  useEventWizardCoverPhoto,
  useEventWizardDescription,
  useEventWizardEndDateTime,
  useEventWizardIsDesignPrefillContentLoading,
  useEventWizardIsRemoveWatermark,
  useEventWizardOrganisers,
  useEventWizardRecurringData,
  useEventWizardRegistrationDetails,
  useEventWizardStartDateTime,
  useEventWizardStyles,
  useEventWizardTimeZone,
  useEventWizardTitle,
  useEventWizardVenue,
} from '@Components/event-wizard/event-wizard.hooks';
import {SkeletonBrick} from '@Components/skeleton-brick';
import styles from './preview.module.scss';
import {EventPageSource, getEventPageURLWithSource} from '@Libraries/events.library';

export function Preview({isMobileScreenSize = false}: PreviewProps): ReactElement<any> {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [isMobilePreview, setIsMobilePreview] = useState<boolean>(true);
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(true);

  const selectedStartDateTime = useEventWizardStartDateTime();
  const selectedEndDateTime = useEventWizardEndDateTime();
  const recurringPattern = useEventWizardRecurringData();
  const timezone = useEventWizardTimeZone();
  const style = useEventWizardStyles();
  const organisers = useEventWizardOrganisers();
  const title = useEventWizardTitle();
  const description = useEventWizardDescription();
  const venue = useEventWizardVenue();
  const coverPhoto = useEventWizardCoverPhoto();
  const registrationForm = useEventWizardRegistrationDetails();
  const isRemoveWatermark = useEventWizardIsRemoveWatermark();
  const isDesignPrefillContentLoading = useEventWizardIsDesignPrefillContentLoading();

  const onIframeLoad = (): void => {
    updatePreviewDateTime({
      startDateTime: selectedStartDateTime,
      endDateTime: selectedEndDateTime,
      recurringPattern: recurringPattern,
      timeZone: timezone,
    });
    updatePreviewCountdown(selectedStartDateTime, timezone);
    updateStyleColor(style.colors);
    updateStyleFont(style.fontFamily);
    updatePreviewOrganiserDetails(organisers.selectedOrganiser);
    updatePreviewWatermark(isRemoveWatermark);

    if (isMobileScreenSize || coverPhoto.uploadedUrl) {
      void updatePreviewCoverPhoto(coverPhoto.uploadedUrl);
    }

    updatePreviewTitle(title);
    updatePreviewDescription(description);
    updatePreviewVenue(venue);

    if (venue.offlineVenue?.address) {
      updatePreviewVenueAddressDetails(venue.offlineVenue.address);
    }
    updatePreviewTimeZone(getGMTLabel(timezone));
    updatePreviewRegistrationForm(registrationForm, title);
    updatePreviewRegistrationCTAText(registrationForm.registerBtnText);
    updatePreviewWatermark(isRemoveWatermark);
    setIsPreviewLoading(false);
  };

  const toggleMobileDesktopPreview = (): void => {
    setIsMobilePreview(!isMobilePreview);
  };

  const getLoadingScreen = (): ReactElement<any> | undefined => {
    return isPreviewLoading || isDesignPrefillContentLoading ? <SkeletonBrick className="_full-height _full-width _position-absolute" /> : undefined;
  };

  return (
    <div className={`${styles.previewSectionContainer} flex-row-align-center`}>
      <div
        className={`border-s-standard ${styles.previewSection} ${isMobilePreview ? 'radius-16' : 'radius-12'} ${isMobileScreenSize ? styles.previewSectionMobileScreen : 'spacing-p-t-10 spacing-p-l-10 spacing-p-r-10 bg-neutral-1'}`}
      >
        <div className={isMobileScreenSize ? '' : styles.previewContainer}>
          {!isMobileScreenSize ? <ResponsivePreviewSwitch isMobileSelected={isMobilePreview} onChange={toggleMobileDesktopPreview} /> : undefined}
          <div className={`${styles.preview} ${!isMobileScreenSize ? styles.previewBorder : ''} ${!isMobileScreenSize && isMobilePreview ? styles.previewMobile : ''}`}>
            <div className={`${styles.iframeContainer} ${!isMobileScreenSize && isMobilePreview ? styles.iframeContainerMobilePreview : styles.iframeContainerPreview}`}>
              {getLoadingScreen()}
              <iframe
                ref={iframeRef}
                id={PREVIEW_IFRAME_ID}
                src={getEventPageURLWithSource(EventPageSource.FOR_WIZARD_IFRAME)}
                onLoad={onIframeLoad}
                title="Example Site"
                className={styles.iframe}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
